/**
 * @fileoverview How the garden is arranged on screen: searched, filtered, sorted, and shown.
 *
 * The grid displayed whatever order the API returned, in one size, which is
 * fine for six plants and unusable for sixty. Everything here is pure — the
 * toolbar holds the settings, this decides what they mean — so the rules can
 * be read in one place and tested without a browser.
 *
 * A plant is searched and sorted by the backend's one name (lib/plantName:
 * "Devil's Ear Lettuce", with its bed when another has the same name), and
 * found by its kind too ("romaine").
 *
 * "Needs you first" and the verdict filter are the backend's (the plants
 * brief, services/plants/plantsBrief; D40): the order comes ready-made and
 * the filter matches the marks it sent. What stays here only arranges the
 * plants the server sent: search over their names, the bed, stage, public,
 * plants and date filters, and the name, stage, bed and recent orders,
 * which read nothing but each plant's own fields.
 *
 * Where: PlantGrid and PlantToolbar (and the filter sheet).
 * API: none.
 * Settings: PLANT_VIEW_STORAGE_KEY (config/tunables/plantList); the address's ?verdict=, ?plants= and ?for=
 * (read by PlantGrid into these settings).
 *
 * @module plantViews
 */

import { recallView, rememberView } from '@/shared/lib/viewMemory';
import { PLANT_VIEW_STORAGE_KEY } from '@/config/tunables/plantList';
import { Plant } from '@/shared/types/plant';
import { serverStampMs } from '@/shared/utils/dateUtils';
import { plantShown } from '@/shared/lib/plantName';
import { passesCanPlantNow } from './canPlantNowFilter';

/** The three densities the garden can be read at. */
export type PlantView = 'cards' | 'grid' | 'list';

/**
 * The orders worth offering. Each maps to a comparator in arrangePlants.
 *
 * "Recently planted" went with the timeline: it read stage.actual_date, which
 * only the timeline ever wrote, so it would now sort every plant by nothing.
 * "recent" is now the most recently touched (lastActivity).
 */
export type PlantSort = 'attention' | 'recent' | 'name' | 'stage' | 'location';

/**
 * What the plants brief says about the garden's needs, for the sort and the
 * verdict filter (the backend decides both: services/plants/plantsBrief).
 */
export interface PlantAttention {
  /** Each plant's place in the "Needs you first" order, first first (the brief's order). */
  place: Map<number, number>;
  /**
   * What the verdict filter matches: the families of each plant's marks, its
   * card's chips. Null when the outlook could not be read: the filter then
   * has nothing to go on and is not applied.
   */
  verdicts: Map<number, Set<string>> | null;
  /**
   * The plants whose planting window includes today, by plant id (the brief's planting.canPlantNow, B84); what
   * the "Can plant now" filter matches. The server decides it; null while the brief is not in.
   */
  plantNow: Set<number> | null;
}

/**
 * What the outlook is doing right now, for anything that has to say so.
 *
 * `checking` is a forecast on its way. `unavailable` is one that is not
 * coming — no postal code to fetch it for, a request that failed, or the
 * archive on screen, where nothing can need attending to. Telling those apart
 * matters: the first is worth a spinner and the second is a spinner that never
 * stops.
 */
export type AttentionState = 'checking' | 'unavailable' | 'known';

/** Which set of plants to draw from: the garden, the archive, or both. */
export type PlantStatusFilter = 'active' | 'archived' | 'all';

/**
 * How far back to look.
 *
 * Named spans rather than two date boxes for the common cases, because
 * "this year" is a question people actually ask and "2026-01-01 to
 * 2026-12-31" is that question typed out. `custom` opens the boxes.
 */
export type DateRangePreset = 'any' | 'days30' | 'days90' | 'thisYear' | 'lastYear' | 'custom';

/** Everything the toolbar can say about which plants to show and how. */
export interface PlantViewSettings {
  /** The density: cards, grid or list. */
  view: PlantView;
  /** The order. */
  sort: PlantSort;
  /** Free text matched against variety, plant and location names. */
  search: string;
  /** A location id, or 'all'. Kept as a string because it comes from a select. */
  location: string;
  /** A stage id, or 'all'. */
  stage: string;
  /** Whether to show the garden, the archive, or everything. */
  status: PlantStatusFilter;
  /** Only the plants whose public page is on. Works with any status. */
  public: boolean;
  /** Only the plants whose planting window includes today (B84, "Can plant now"); the server says which. */
  canPlantNow: boolean;
  /**
   * One of the week's marks, keyed by its chip family ("harm-chill",
   * "danger-PROTECT_FROST", "needs_water"), or '' for all. Set by "Show
   * plants" in the weather bar, a chip in the sheet, or a link carrying
   * ?verdict=. It replaced a filter by severity band and direction, which filed
   * each plant under one direction only, so a plant both warm and cold could
   * be found under one of them.
   */
  verdict: string;
  /**
   * Only these plants, by id, comma-separated, or '' for all: the plants a
   * Weather coming up rule's line on Today is about (?plants=), with the line
   * to name them by (plantsFor, ?for=).
   */
  plantIds: string;
  /** The line those plants are named by on the page ("Chilly Tue night"); '' for none. */
  plantsFor: string;
  /** How far back to look, by when each plant was last touched (lastActivity). */
  dateRange: DateRangePreset;
  /** Only read when dateRange is 'custom'. ISO days, or the empty string. */
  dateFrom: string;
  /** The end of a custom range, the same way. */
  dateTo: string;
}

/** The garden as it first opens: cards, needs-you-first, every active plant. */
export const DEFAULT_VIEW_SETTINGS: PlantViewSettings = {
  view: 'cards',
  sort: 'attention',
  search: '',
  location: 'all',
  stage: 'all',
  status: 'active',
  public: false,
  canPlantNow: false,
  verdict: '',
  plantIds: '',
  plantsFor: '',
  dateRange: 'any',
  dateFrom: '',
  dateTo: '',
};

/**
 * How many filters are currently narrowing the garden.
 *
 * The toolbar shows this on the filter button, so a garden quietly hiding half
 * of itself says so on the surface rather than only inside the sheet.
 *
 * Search is counted: it narrows like the rest, and typing in it then scrolling
 * away is the easiest way to lose track of why a plant is missing.
 *
 * @param settings - What the toolbar currently says.
 */
export function activeFilterCount(settings: PlantViewSettings): number {
  let n = 0;
  if (settings.search.trim()) n += 1;
  if (settings.location !== 'all') n += 1;
  if (settings.stage !== 'all') n += 1;
  if (settings.status !== 'active') n += 1;
  if (settings.public) n += 1;
  if (settings.canPlantNow) n += 1;
  if (settings.verdict) n += 1;
  if (settings.plantIds) n += 1;
  if (settings.dateRange !== 'any') n += 1;
  return n;
}

/** Everything back to showing the whole garden, leaving view and sort alone. */
export const CLEARED_FILTERS: Partial<PlantViewSettings> = {
  search: '', location: 'all', stage: 'all', status: 'active', public: false, canPlantNow: false, verdict: '',
  plantIds: '', plantsFor: '', dateRange: 'any', dateFrom: '', dateTo: '',
};

/**
 * The settings this browser last used.
 *
 * Only the three that are worth outliving a page load are restored. A search
 * term and a filter are about right now — coming back tomorrow to a garden
 * that silently shows four of your twenty plants is a bug report, not a
 * convenience.
 *
 * @returns Stored view and sort where valid, defaults otherwise.
 */
export function loadViewSettings(): PlantViewSettings {
  return {
    ...DEFAULT_VIEW_SETTINGS,
    ...recallView<PlantViewSettings>(PLANT_VIEW_STORAGE_KEY, { view: isView, sort: isSort }),
  };
}

/**
 * Remembers the two settings worth remembering.
 *
 * @param settings - What the toolbar currently says; only view and sort are kept.
 */
export function saveViewSettings(settings: PlantViewSettings): void {
  rememberView(PLANT_VIEW_STORAGE_KEY, { view: settings.view, sort: settings.sort });
}

/**
 * Whether a remembered value is a view the page still has.
 *
 * @param value - What storage held.
 */
const isView = (value: unknown): value is PlantView =>
  value === 'cards' || value === 'grid' || value === 'list';

/**
 * Whether a remembered value is an order the page still has.
 *
 * @param value - What storage held.
 */
const isSort = (value: unknown): value is PlantSort =>
  value === 'attention' || value === 'recent' || value === 'name' || value === 'stage'
  || value === 'location';

/**
 * Case- and accent-insensitive comparison, in the reader's own alphabet.
 *
 * localeCompare rather than < so that "Ñora" sorts where a Spanish speaker
 * expects it to and "ámbar" does not land after "zucchini".
 *
 * @param a - One name.
 * @param b - The other.
 */
const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * The span a date preset stands for, as milliseconds since the epoch.
 *
 * Open at either end: a null bound means "no limit that way", which is what
 * makes "any" and a half-filled custom range work without a special case.
 *
 * @param settings - The toolbar's current date choice.
 * @returns The earliest and latest moment to include.
 */
export function dateBounds(settings: PlantViewSettings): { from: number | null; to: number | null } {
  const now = new Date();
  /**
   * The moment this many days ago.
   *
   * @param count - Days.
   */
  const days = (count: number): number => now.getTime() - count * 24 * 60 * 60 * 1000;
  /**
   * Midnight on the first of January, the gardener's own clock.
   *
   * @param year - The year.
   */
  const yearStart = (year: number): number => new Date(year, 0, 1).getTime();

  switch (settings.dateRange) {
    case 'days30': return { from: days(30), to: null };
    case 'days90': return { from: days(90), to: null };
    case 'thisYear': return { from: yearStart(now.getFullYear()), to: null };
    case 'lastYear': return {
      from: yearStart(now.getFullYear() - 1),
      to: yearStart(now.getFullYear()) - 1,
    };
    case 'custom': {
      /**
       * A date box's day as the gardener's own midnight, or null when empty or
       * unreadable. A bare "YYYY-MM-DD" is given a time, because Date.parse
       * reads a bare date as UTC midnight, which moved the range by the
       * gardener's offset from UTC (an evening on the end day fell outside it).
       */
      const parse = (value: string): number | null => {
        if (!value) return null;
        const when = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
        return Number.isNaN(when) ? null : when;
      };
      const to = parse(settings.dateTo);
      // An end date means the whole of that day, not midnight at the start of
      // it — otherwise choosing today as the end excludes everything today.
      return { from: parse(settings.dateFrom), to: to === null ? null : to + 24 * 60 * 60 * 1000 - 1 };
    }
    case 'any':
    default:
      return { from: null, to: null };
  }
}

/**
 * When anything last happened to this plant, as far as the card can tell.
 *
 * Not date_updated: that column comes back null for every plant in the
 * garden, so sorting by it silently did nothing and called itself "recently
 * updated". The plant's own contents are the honest answer — the newest
 * photo, the newest note, the day the current stage began — and whichever of
 * those is latest is the last time the gardener touched this plant.
 *
 * @param plant - The plant to date.
 * @returns Milliseconds since the epoch, or NaN when nothing is dated.
 */
export function lastActivity(plant: Plant): number {
  let latest = NaN;
  /**
   * Keeps a date if it is readable and later than any seen so far.
   *
   * @param value - A server stamp, or nothing.
   */
  const consider = (value: string | undefined | null): void => {
    if (!value) return;
    const when = serverStampMs(value);
    if (!Number.isNaN(when) && (Number.isNaN(latest) || when > latest)) latest = when;
  };

  consider(plant.date_updated);
  // Adding a plant is the first thing anyone does to it. Without this a new
  // plant had no date at all and sank below everything under "recent" —
  // under the fold on a phone, right after you pressed Save.
  consider(plant.created_at);
  for (const photo of plant.photos ?? []) consider(photo.date_uploaded ?? photo.date_taken);
  for (const note of plant.notes ?? []) consider(note.timestamp);

  return latest;
}

/**
 * Newest first, with anything undated last rather than pretending to be old.
 *
 * @param left - One moment in milliseconds, or NaN.
 * @param right - The other.
 */
const byTimeDesc = (left: number, right: number): number => {
  if (Number.isNaN(left) && Number.isNaN(right)) return 0;
  if (Number.isNaN(left)) return 1;
  if (Number.isNaN(right)) return -1;
  return right - left;
};

/**
 * Applies a toolbar's settings to the garden.
 *
 * Filtering first, then sorting, on a copy: the array belongs to the query
 * cache and sorting it in place would reorder what every other reader sees.
 *
 * @param plants - The garden as fetched, or undefined before it arrives.
 * @param settings - What the toolbar currently says.
 * @param attention - What the plants brief says: each plant's place in the
 *   "Needs you first" order and its marks' families. Null while the brief is
 *   still on its way (or cannot be had). It comes from a different query than
 *   the garden does, so it is passed in rather than read here — this stays a
 *   pure function of its arguments, and the caller owns the fetching.
 *
 *   Null means the question cannot be answered yet, and an unanswerable filter
 *   is not applied: showing an empty garden for a second while the advice
 *   loads would read as a garden with nothing in it.
 * @returns The plants to render, in the order to render them.
 */
export function arrangePlants(
  plants: Plant[] | undefined,
  settings: PlantViewSettings,
  attention: PlantAttention | null = null
): Plant[] {
  if (!plants) return [];

  /*
   * The words somebody typed, each of which has to turn up somewhere.
   *
   * The whole query used to be tested against each name on its own, so it only
   * ever matched a phrase living inside one field. "Butternut squash" — the
   * plainest thing a person types for a Waltham Butternut in the Squash Bed —
   * found nothing, because "butternut" is the variety and "squash" is the crop
   * and neither field contains both. Every single word still behaves exactly
   * as it did.
   */
  const needles = settings.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const { from, to } = dateBounds(settings);

  const shown = plants.filter((plant) => {
    // The payload has been known to carry holes; the grid used to check for
    // them at render time and skip. Cheaper to drop them once, here.
    if (!plant) return false;
    if (settings.location !== 'all' && String(plant.location?.id) !== settings.location) {
      return false;
    }
    if (settings.stage !== 'all' && String(plant.stage?.id) !== settings.stage) {
      return false;
    }
    if (settings.public && !(plant.is_public === 1 || plant.is_public === true)) return false;
    // The plants the server says have a window open today (B84); until the brief is in, nothing is filtered away.
    if (settings.canPlantNow && !passesCanPlantNow(plant.plant_obj_id, attention?.plantNow ?? null)) return false;
    // One of the week's verdicts. Until the outlook has answered, the filter
    // has nothing to go on and is not applied: an empty garden for a second
    // while the advice loads would read as a garden with nothing in it.
    if (settings.verdict && attention?.verdicts) {
      if (!attention.verdicts.get(plant.plant_obj_id)?.has(settings.verdict)) return false;
    }
    // The plants a Coming up rule's line is about.
    if (settings.plantIds && !settings.plantIds.split(',').includes(String(plant.plant_obj_id))) return false;
    if (from !== null || to !== null) {
      const when = lastActivity(plant);
      // A plant nothing is known about cannot be placed in time, so a date
      // filter has nothing to say about it and it is left out rather than
      // silently counted as today.
      if (Number.isNaN(when)) return false;
      if (from !== null && when < from) return false;
      if (to !== null && when > to) return false;
    }
    if (needles.length === 0) return true;
    /*
     * One haystack, so a query may cross the names it is made of.
     *
     * The stage is in here too: "seedling squash" is a reasonable thing to
     * look for and used to find nothing at all.
     */
    // The plant's one name (lib/plantName) with its kind, so "romaine" still finds a Devil's Ear lettuce.
    const haystack = [
      plantShown(plant), plant.names.kindName, plant.plant?.name, plant.location?.name, plant.stage?.name,
    ].filter(Boolean).join(' ').toLowerCase();
    return needles.every((word) => haystack.includes(word));
  });

  const sorted = [...shown];
  switch (settings.sort) {
    case 'attention':
      // What needs you first, as the plants brief orders it (services/plants/
      // attentionOrder: a plant added in the last day, then a danger, then
      // anything else to act on, then the rest, each the most recently
      // touched first). A plant the brief does not know yet was added since
      // it was read, and leads. Without a brief, the most recently touched
      // first, as "recent" would have it.
      if (attention) {
        /**
         * A plant's place, before every known one when the brief does not know it.
         *
         * @param plant - The plant.
         */
        const placeOf = (plant: Plant): number => attention.place.get(plant.plant_obj_id) ?? -1;
        sorted.sort((a, b) => placeOf(a) - placeOf(b));
      } else {
        sorted.sort((a, b) => byTimeDesc(lastActivity(a), lastActivity(b)));
      }
      break;
    case 'name':
      sorted.sort((a, b) => byName(plantShown(a), plantShown(b)));
      break;
    case 'stage':
      // Within a stage, alphabetical — otherwise every seedling arrives in
      // whatever order the database felt like.
      sorted.sort((a, b) =>
        (a.stage?.id ?? 0) - (b.stage?.id ?? 0)
        || byName(plantShown(a), plantShown(b)));
      break;
    case 'location':
      sorted.sort((a, b) =>
        byName(a.location?.name ?? '', b.location?.name ?? '')
        || byName(plantShown(a), plantShown(b)));
      break;
    case 'recent':
    default:
      sorted.sort((a, b) => byTimeDesc(lastActivity(a), lastActivity(b)));
      break;
  }

  return sorted;
}

/**
 * The locations actually represented in the garden, named and de-duplicated.
 *
 * Built from the plants rather than fetched, so the filter can never offer a
 * location that would show nothing.
 *
 * @param plants - The garden, or undefined before it arrives.
 */
export function locationsInUse(plants: Plant[] | undefined): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>();
  for (const plant of plants ?? []) {
    if (plant.location?.id != null) seen.set(plant.location.id, plant.location.name);
  }
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => byName(a.name, b.name));
}

/**
 * The stages actually represented in the garden, in growth order.
 *
 * @param plants - The garden, or undefined before it arrives.
 */
export function stagesInUse(plants: Plant[] | undefined): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>();
  for (const plant of plants ?? []) {
    const stage = plant.stage;
    if (stage) seen.set(stage.id, stage.name);
  }
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.id - b.id);
}
